// Chop verb — slice a longer source into N samples.
//
// Self-contained flow (diverges from the shell's single-buffer pipeline
// because chop produces N samples, not one):
//   1. Load a source via file picker.
//   2. Run auto-chop (or add manual slice markers).
//   3. Name the pack; save all slices as <base>_0, <base>_1, ….
//
// Output slices are stored as individual Sample rows with shared
// `basename`-prefixed names. They can be played via s("basename:0"),
// s("basename:1"), … from the pattern (superdough's string-array
// behaviour kicks in once all the numbered sibling names are registered).

import { useCallback, useMemo, useRef, useState } from 'react';
import { decodeAudioFile } from '../../import';
import { commit } from '../../commit';
import { detectOnsets, onsetsToSlices } from './onsetDetection';
import { WaveformPad } from '../../ui/WaveformPad';
import { pcmFromChannels, fromPcm, trim } from '../../pipeline';
import type { VerbProps } from '../types';

export function ChopVerb({ audioContext, storage, onStatus }: VerbProps) {
  const [sourceBuffer, setSourceBuffer] = useState<AudioBuffer | null>(null);
  const [sourceFilename, setSourceFilename] = useState('');
  const [onsets, setOnsets] = useState<number[]>([]);
  const [threshold, setThreshold] = useState(1.8);
  const [minGapMs, setMinGapMs] = useState(50);
  const [baseName, setBaseName] = useState('chop');
  const [committing, setCommitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const slices = useMemo(
    () => (sourceBuffer ? onsetsToSlices(onsets, sourceBuffer.length) : []),
    [sourceBuffer, onsets],
  );

  const loadFile = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      if (!audioContext) {
        onStatus('engine not booted — press Play first', 'warn');
        return;
      }
      const f = files.item(0);
      if (!f) return;
      const result = await decodeAudioFile(f, audioContext);
      if (!result.ok) {
        onStatus(`Load rejected: ${result.reason}`, 'err');
        return;
      }
      setSourceBuffer(result.buffer);
      setSourceFilename(result.filename);
      setBaseName(result.suggestedName);
      setOnsets([]);
      onStatus(
        `Loaded ${result.filename} (${result.buffer.duration.toFixed(1)}s). Auto-chop or click markers.`,
        'ok',
      );
    },
    [audioContext, onStatus],
  );

  const autoChop = useCallback(() => {
    if (!sourceBuffer) return;
    const ch = sourceBuffer.getChannelData(0);
    const detected = detectOnsets(ch, sourceBuffer.sampleRate, {
      thresholdRatio: threshold,
      minGapSeconds: minGapMs / 1000,
    });
    setOnsets(detected);
    onStatus(`Detected ${detected.length} onsets.`, detected.length > 0 ? 'ok' : 'warn');
  }, [sourceBuffer, threshold, minGapMs, onStatus]);

  const clearOnsets = useCallback(() => setOnsets([]), []);

  const handleMarkerClick = useCallback(
    (_kind: 'in' | 'out', frame: number) => {
      // Shift-click already sets out in WaveformPad; we repurpose markers
      // in chop mode so in=add-slice-here. Later polish: toggle remove
      // when clicking on an existing marker.
      setOnsets((prev) => {
        const next = [...prev, frame].sort((a, b) => a - b);
        // Dedupe near-duplicates (within 10ms).
        const minGap = sourceBuffer ? Math.round(0.01 * sourceBuffer.sampleRate) : 0;
        const filtered: number[] = [];
        for (const o of next) {
          if (filtered.length === 0 || o - filtered[filtered.length - 1]! > minGap) {
            filtered.push(o);
          }
        }
        return filtered;
      });
    },
    [sourceBuffer],
  );

  const saveAll = useCallback(async () => {
    if (!sourceBuffer || !audioContext || !storage) return;
    if (slices.length === 0) {
      onStatus('No slices to save — run auto-chop or click to add markers.', 'warn');
      return;
    }
    const base = baseName.trim() || 'chop';
    // One parentId per chop operation so all sibling slices share it —
    // enables "show me everything that came from this chop" in later
    // provenance views (D4). Computed once, not per-iteration.
    const parentId = 'chop-' + Date.now();
    setCommitting(true);
    const pcm = pcmFromChannels(
      sourceBuffer.sampleRate,
      Array.from({ length: sourceBuffer.numberOfChannels }, (_, c) => {
        const src = sourceBuffer.getChannelData(c);
        const copy = new Float32Array(src.length);
        copy.set(src);
        return copy;
      }),
    );
    try {
      for (let i = 0; i < slices.length; i++) {
        const { start, end } = slices[i]!;
        const slicePcm = trim(pcm, {
          startSeconds: start / sourceBuffer.sampleRate,
          endSeconds: end / sourceBuffer.sampleRate,
        });
        const sliceBuffer = fromPcm(audioContext, slicePcm);
        await commit(
          {
            buffer: sliceBuffer,
            name: `${base}_${i}`,
            origin: {
              kind: 'chop',
              parentId,
              sliceIndex: i,
            },
          },
          storage,
          audioContext,
        );
      }
      onStatus(
        `Saved ${slices.length} slices as ${base}_0 … ${base}_${slices.length - 1}.`,
        'ok',
      );
      // Reset to let the user chop another.
      setSourceBuffer(null);
      setSourceFilename('');
      setOnsets([]);
    } catch (e) {
      onStatus(
        'Save failed: ' + (e instanceof Error ? e.message : String(e)),
        'err',
      );
    } finally {
      setCommitting(false);
    }
  }, [sourceBuffer, audioContext, storage, slices, baseName, onStatus]);

  if (!sourceBuffer) {
    return (
      <div className="flex flex-col gap-4 min-h-[200px]">
        <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-400">
          Load a longer source — a break, a loop, a field recording — and slice it into samples.
        </div>
        <div className="flex items-center justify-center border-2 border-dashed border-zinc-700 rounded-lg p-10 min-h-[150px]">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold"
          >
            Choose source file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            style={{ display: 'none' }}
            onChange={(e) => void loadFile(e.target.files)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 min-h-[200px]">
      <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 px-4 py-2 text-xs text-zinc-400">
        <span>
          {sourceFilename} · {sourceBuffer.duration.toFixed(2)}s · {slices.length} slice
          {slices.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={() => {
            setSourceBuffer(null);
            setOnsets([]);
          }}
          className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-200"
        >
          ← change source
        </button>
      </div>

      <WaveformPad
        buffer={sourceBuffer}
        inMarkerFrame={0}
        outMarkerFrame={sourceBuffer.length}
        slices={onsets}
        onMarkerChange={handleMarkerClick}
        audioContext={audioContext}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <label className="flex items-center gap-2">
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500 w-28">
            Threshold
          </span>
          <input
            type="range"
            min={1.1}
            max={4}
            step={0.1}
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            className="accent-yellow-300 flex-1"
          />
          <span className="tabular-nums w-12 text-right text-zinc-300">
            {threshold.toFixed(1)}×
          </span>
        </label>
        <label className="flex items-center gap-2">
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500 w-28">
            Min gap
          </span>
          <input
            type="range"
            min={10}
            max={500}
            step={1}
            value={minGapMs}
            onChange={(e) => setMinGapMs(Number(e.target.value))}
            className="accent-yellow-300 flex-1"
          />
          <span className="tabular-nums w-14 text-right text-zinc-300">
            {minGapMs}ms
          </span>
        </label>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={autoChop}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700"
        >
          Auto-chop
        </button>
        <button
          type="button"
          onClick={clearOnsets}
          disabled={onsets.length === 0}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-zinc-800 border border-zinc-700 text-zinc-200 disabled:opacity-30"
        >
          Clear markers
        </button>
        <label className="flex items-center gap-2">
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500">Base name</span>
          <input
            type="text"
            value={baseName}
            onChange={(e) => setBaseName(e.target.value)}
            className="bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-zinc-100 text-sm focus:border-yellow-300 focus:outline-none min-w-[120px]"
          />
        </label>
        <button
          type="button"
          onClick={() => void saveAll()}
          disabled={slices.length === 0 || committing}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
        >
          {committing ? 'Saving…' : `Save ${slices.length} slice${slices.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}
